import type {
  DataTableCellPreSelection,
  DataTableColumn,
  DataTableSelection,
  JsonData,
  JsonDataItem,
  OdinKey,
} from '@myosh/odin-components';
import { DataGridDataCell, DataTable, Dialog } from '@myosh/odin-components';
import type { Ref } from 'react';
import { forwardRef, useCallback, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Observable, Subject } from 'rxjs';
import type { AuditLogData } from '../../../@types/audit';
import type { CombinedFieldType } from '../../../@types/forms';
import { useLazyGetAuditLogsQuery } from '../../../redux/services/record';
import { forceAssert } from '../../../utils/type-guards';
import ConflictsFooter from './conflicts-footer.component';
import ConflictsHeader from './conflicts-header.component';
import { formatSelectionData, resolvedFieldConflicts, selectColumn } from './conflicts-modal-util';
import ConflictsTableCell from './table-cells/conflicts-table-cell.component';

export interface Conflict {
  id: OdinKey;
  caption: string;
  type: CombinedFieldType;
  customFieldType?: string;
  theirValue: unknown;
  myValue: unknown;
  dynamicProperties?: JsonDataItem;
}

export const Columns = {
  Theirs: 1, // original
  Mine: 2, // new
} as const;
export type Columns = (typeof Columns)[keyof typeof Columns];

interface ConflictModalState {
  visible: boolean;
  data: Conflict[];
  selectedCells: Array<DataTableCellPreSelection>;
  lastModifiedBy?: string;
}

const initialState: ConflictModalState = {
  visible: false,
  data: [],
  selectedCells: [],
};

export interface ConflictResolution {
  fields: ConflictResolutionItem[];
}

export interface ConflictResolutionItem {
  id: OdinKey;
  value: unknown;
}

interface ConflictModalProps {
  recordId?: string;
}

export interface ConflictsModalRef {
  show: (data: Conflict[]) => Observable<ConflictResolution>;
}

const ConflictsModal = ({ recordId }: ConflictModalProps, ref: Ref<ConflictsModalRef>) => {
  const [state, setState] = useState<ConflictModalState>(initialState);
  const resolved = state.data.length === state.selectedCells.length;
  const conflictResolutionRef = useRef<Subject<ConflictResolution>>(new Subject<ConflictResolution>());
  const [getAuditLogs] = useLazyGetAuditLogsQuery();
  const { t } = useTranslation();

  const handleSelectionChange = useCallback((selection: Array<DataTableSelection>) => {
    setState((state) => {
      return {
        ...state,
        selectedCells: selection.map((item) => ({
          rowIndex: item.rowIndex,
          cellIndex: item.cellIndex,
        })),
      };
    });
  }, []);

  const handleIsCellSelectable = useCallback((field: string) => {
    return field !== 'caption';
  }, []);

  const showDialog = useCallback(async (data: Conflict[]) => {
    let lastModifiedBy: string;
    try {
      const auditData: AuditLogData[] = await getAuditLogs({
        recordId: recordId as string,
        page: 1,
        pageSize: 1,
        sortByVersion: 'desc',
      }).unwrap();

      lastModifiedBy = auditData[0]?.user;
    } catch {
      // Ignore error - lastModifiedBy will remain undefined
    }

    setState((state) => {
      return {
        ...state,
        data,
        visible: true,
        selectedCells: selectColumn(data, Columns.Mine),
        lastModifiedBy,
      };
    });
  }, []);

  const hideDialog = useCallback(() => {
    setState((state) => {
      return {
        ...state,
        visible: false,
      };
    });

    conflictResolutionRef.current.complete();
  }, []);

  const handleClose = useCallback(() => {
    setState(initialState);

    conflictResolutionRef.current.next({ fields: [] });
    conflictResolutionRef.current.complete();
  }, []);

  const handleSave = () => {
    const _resolvedFieldConflicts = resolvedFieldConflicts(state.selectedCells, state.data);

    conflictResolutionRef.current.next({
      fields: _resolvedFieldConflicts,
    });

    hideDialog();
  };

  const show = (data: Conflict[]) => {
    showDialog(data);
    return conflictResolutionRef.current.asObservable();
  };

  useImperativeHandle(ref, () => ({
    show,
  }));

  const header = <ConflictsHeader lastModifiedBy={state.lastModifiedBy} />;

  const footer = <ConflictsFooter resolved={resolved} onSave={handleSave} />;

  const conflictCell = (rowData: JsonDataItem, column: Columns) => {
    const conflict = rowData as unknown as Conflict;
    const value = column === Columns.Mine ? conflict.myValue : conflict.theirValue;
    return (
      <ConflictsTableCell
        recordId={recordId}
        value={value}
        type={conflict.type}
        customFieldType={conflict.customFieldType}
        dynamicProperties={conflict.dynamicProperties}
        width={290}
      />
    );
  };

  const columns: Array<DataTableColumn> = [
    {
      field: 'caption',
      header: t('field'),
      customCell: (rowData: JsonDataItem) => <DataGridDataCell value={rowData.caption} width={180} />,
    },
    {
      field: 'theirValue',
      header: t('theirs'),
      customCell: (rowData: JsonDataItem) => conflictCell(rowData, Columns.Theirs),
    },
    {
      field: 'myValue',
      header: t('mine'),
      customCell: (rowData: JsonDataItem) => conflictCell(rowData, Columns.Mine),
    },
  ];

  return (
    <>
      {state.visible && (
        <Dialog
          header={header}
          footer={footer}
          onClose={handleClose}
          visible={state.visible}
          containerClassName="md:max-w-[800px]"
        >
          <DataTable
            data={forceAssert<JsonData>(state.data)}
            columns={columns}
            preSelected={state.selectedCells}
            onSelectionChange={handleSelectionChange}
            onModifySelection={formatSelectionData}
            handleIsDataSelectable={handleIsCellSelectable}
          />
        </Dialog>
      )}
    </>
  );
};

export default forwardRef(ConflictsModal);
